import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { RadioGroup } from './RadioGroup';
import { Select } from './Select';
import { CheckboxGroup } from './CheckboxGroup';
import { TextInput } from './TextInput';
import { Tooltip } from './Tooltip';
import { ProgressBar } from './ProgressBar';
import { QuizData } from '../utils/ValuationCalculator';
import { sendToGoHighLevel } from '../utils/ghlIntegration';
import { useUTMCapture } from '../hooks/useUTMTracking';
import { saveQuizData, getQuizData } from '../utils/quizDataPersistence';
import { buildGHLIframeSrc } from '../utils/buildGHLIframeSrc';
import useSound from 'use-sound';
// Define the question interface with section
interface Question {
  id: keyof QuizData | 'contact';
  type: 'radio' | 'select' | 'checkbox' | 'text';
  section: string;
  label: string;
  tooltip?: string;
  placeholder?: string;
  options?: Array<{
    value: string;
    label: string;
  }>;
}
export function QuizForm() {
  const navigate = useNavigate();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stepDirection, setStepDirection] = useState<'forward' | 'back'>('forward');
  const [hasAutoAdvanced, setHasAutoAdvanced] = useState(false);
  // Sound effect for completing a question
  const [playDing] = useSound('/ding.mp3', {
    volume: 0.5
  });
  // Capture UTM parameters on component mount
  useUTMCapture();
  // Define all questions in sequence with sections
  const questions: Question[] = [{
    id: 'industry',
    type: 'select',
    section: 'About Your Practice',
    label: 'What type of practice do you own?',
    options: [{
      value: 'chiropractic',
      label: 'Chiropractic'
    }, {
      value: 'dental',
      label: 'Dental'
    }, {
      value: 'medspa',
      label: 'Med Spa'
    }, {
      value: 'physical-therapy',
      label: 'Physical Therapy'
    }, {
      value: 'other',
      label: 'Other'
    }]
  }, {
    id: 'isOwner',
    type: 'radio',
    section: 'About You',
    label: 'Are you the owner?',
    options: [{
      value: 'yes',
      label: 'Yes'
    }, {
      value: 'no',
      label: 'No'
    }]
  }, {
    id: 'state',
    type: 'select',
    section: 'About You',
    label: 'What state is your clinic in?',
    options: [{
      value: 'AL',
      label: 'Alabama'
    }, {
      value: 'AK',
      label: 'Alaska'
    }, {
      value: 'AZ',
      label: 'Arizona'
    }, {
      value: 'AR',
      label: 'Arkansas'
    }, {
      value: 'CA',
      label: 'California'
    }, {
      value: 'CO',
      label: 'Colorado'
    }, {
      value: 'CT',
      label: 'Connecticut'
    }, {
      value: 'DE',
      label: 'Delaware'
    }, {
      value: 'FL',
      label: 'Florida'
    }, {
      value: 'GA',
      label: 'Georgia'
    }, {
      value: 'HI',
      label: 'Hawaii'
    }, {
      value: 'ID',
      label: 'Idaho'
    }, {
      value: 'IL',
      label: 'Illinois'
    }, {
      value: 'IN',
      label: 'Indiana'
    }, {
      value: 'IA',
      label: 'Iowa'
    }, {
      value: 'KS',
      label: 'Kansas'
    }, {
      value: 'KY',
      label: 'Kentucky'
    }, {
      value: 'LA',
      label: 'Louisiana'
    }, {
      value: 'ME',
      label: 'Maine'
    }, {
      value: 'MD',
      label: 'Maryland'
    }, {
      value: 'MA',
      label: 'Massachusetts'
    }, {
      value: 'MI',
      label: 'Michigan'
    }, {
      value: 'MN',
      label: 'Minnesota'
    }, {
      value: 'MS',
      label: 'Mississippi'
    }, {
      value: 'MO',
      label: 'Missouri'
    }, {
      value: 'MT',
      label: 'Montana'
    }, {
      value: 'NE',
      label: 'Nebraska'
    }, {
      value: 'NV',
      label: 'Nevada'
    }, {
      value: 'NH',
      label: 'New Hampshire'
    }, {
      value: 'NJ',
      label: 'New Jersey'
    }, {
      value: 'NM',
      label: 'New Mexico'
    }, {
      value: 'NY',
      label: 'New York'
    }, {
      value: 'NC',
      label: 'North Carolina'
    }, {
      value: 'ND',
      label: 'North Dakota'
    }, {
      value: 'OH',
      label: 'Ohio'
    }, {
      value: 'OK',
      label: 'Oklahoma'
    }, {
      value: 'OR',
      label: 'Oregon'
    }, {
      value: 'PA',
      label: 'Pennsylvania'
    }, {
      value: 'RI',
      label: 'Rhode Island'
    }, {
      value: 'SC',
      label: 'South Carolina'
    }, {
      value: 'SD',
      label: 'South Dakota'
    }, {
      value: 'TN',
      label: 'Tennessee'
    }, {
      value: 'TX',
      label: 'Texas'
    }, {
      value: 'UT',
      label: 'Utah'
    }, {
      value: 'VT',
      label: 'Vermont'
    }, {
      value: 'VA',
      label: 'Virginia'
    }, {
      value: 'WA',
      label: 'Washington'
    }, {
      value: 'WV',
      label: 'West Virginia'
    }, {
      value: 'WI',
      label: 'Wisconsin'
    }, {
      value: 'WY',
      label: 'Wyoming'
    }]
  }, {
    id: 'yearsInBusiness',
    type: 'text',
    section: 'About Your Practice',
    label: 'How many years has your practice been in business?',
    placeholder: 'e.g., 5'
  }, {
    id: 'revenue',
    type: 'select',
    section: 'Financial Details',
    label: "What was your clinic's revenue last year?",
    options: [{
      value: '0-1M',
      label: '$0–$1M'
    }, {
      value: '1-3M',
      label: '$1M–$3M'
    }, {
      value: '3-5M',
      label: '$3M–$5M'
    }, {
      value: '5-10M',
      label: '$5M–$10M'
    }, {
      value: '10-20M',
      label: '$10M–$20M'
    }]
  }, {
    id: 'profit',
    type: 'select',
    section: 'Financial Details',
    label: 'About how much money was left over after paying staff and bills?',
    tooltip: "Don't worry if it's rough—pick a range. This is the profit margin (money left after all expenses).",
    options: [{
      value: '0-10',
      label: '0–10%'
    }, {
      value: '10-15',
      label: '10–15%'
    }, {
      value: '15-25',
      label: '15–25%'
    }, {
      value: '25+',
      label: '25%+'
    }, {
      value: 'not-sure',
      label: "I'm not sure"
    }]
  }, {
    id: 'payerMix',
    type: 'select',
    section: 'Financial Details',
    label: 'What percentage of your revenue is insurance vs cash pay?',
    options: [{
      value: '100-cash',
      label: '100% Cash / No Insurance'
    }, {
      value: '0-25-insurance',
      label: '0–25% Insurance / 75–100% Cash'
    }, {
      value: '25-50-insurance',
      label: '25–50% Insurance / 50–75% Cash'
    }, {
      value: '50-75-insurance',
      label: '50–75% Insurance / 25–50% Cash'
    }, {
      value: '75-100-insurance',
      label: '75–100% Insurance / 0–25% Cash'
    }, {
      value: '100-insurance',
      label: '100% Insurance / No Cash'
    }]
  }, {
    id: 'locations',
    type: 'select',
    section: 'Operations',
    label: 'How many locations do you have?',
    options: [{
      value: '1',
      label: '1'
    }, {
      value: '2-3',
      label: '2–3'
    }, {
      value: '4-5',
      label: '4–5'
    }, {
      value: '6+',
      label: '6+'
    }]
  }, {
    id: 'providers',
    type: 'select',
    section: 'Operations',
    label: 'How many providers see patients?',
    options: [{
      value: '1',
      label: '1'
    }, {
      value: '2-3',
      label: '2–3'
    }, {
      value: '4-5',
      label: '4–5'
    }, {
      value: '5+',
      label: '5+'
    }]
  }, {
    id: 'ownerHours',
    type: 'select',
    section: 'Operations',
    label: 'How many hours per week do you personally see patients?',
    tooltip: "This helps us understand 'key man risk' — how dependent your clinic is on you personally. Less dependency means higher value.",
    options: [{
      value: '40+',
      label: "40+ hours — I'm the main provider"
    }, {
      value: '20-40',
      label: '20–40 hours — I share patient load'
    }, {
      value: '<20',
      label: '<20 hours — associates handle most visits'
    }, {
      value: '0',
      label: "I don't see patients — I oversee operations only"
    }]
  }, {
    id: 'concentration',
    type: 'radio',
    section: 'Risk Factors',
    label: 'Is any single insurer or referral source over 30% of your patients?',
    options: [{
      value: 'yes',
      label: 'Yes'
    }, {
      value: 'no',
      label: 'No'
    }]
  }, {
    id: 'trend',
    type: 'select',
    section: 'Growth Potential',
    label: "What's your 3-year revenue trend?",
    options: [{
      value: 'down',
      label: 'Down'
    }, {
      value: 'flat',
      label: 'Flat'
    }, {
      value: 'up-5-10',
      label: 'Up 5–10%'
    }, {
      value: 'up-10-20',
      label: 'Up 10–20%'
    }, {
      value: 'up-20+',
      label: 'Up 20%+'
    }]
  }, {
    id: 'timeline',
    type: 'select',
    section: 'Exit Planning',
    label: 'When are you thinking about selling?',
    options: [{
      value: '0-12m',
      label: '0–12 months'
    }, {
      value: '12-24m',
      label: '12–24 months'
    }, {
      value: '24m+',
      label: '24+ months'
    }]
  }, {
    id: 'contact',
    type: 'select',
    section: 'Get Your Results',
    label: 'Contact Information',
    options: []
  }];
  const totalQuestions = questions.length;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  /**
   * CRITICAL: GHL iframe src with tracking parameters
   */
  const [ghlIframeSrc, setGhlIframeSrc] = useState<string>('');
  // Initialize form data from localStorage (if exists) or empty state
  const [formData, setFormData] = useState<QuizData & {
    firstName: string;
    lastName: string;
    email: string;
    mobile: string;
    smsConsent: boolean;
  }>(() => {
    // Try to restore from localStorage on initial load
    const savedData = getQuizData();
    return {
      industry: savedData.industry || '',
      industryOther: savedData.industryOther || '',
      isOwner: savedData.isOwner || '',
      state: savedData.state || '',
      yearsInBusiness: savedData.yearsInBusiness || '',
      revenue: savedData.revenue || '',
      profit: savedData.profit || '',
      locations: savedData.locations || '',
      providers: savedData.providers || '',
      payerMix: savedData.payerMix || '',
      concentration: savedData.concentration || '',
      trend: savedData.trend || '',
      timeline: savedData.timeline || '',
      ownerHours: savedData.ownerHours || '',
      firstName: '',
      lastName: '',
      email: '',
      mobile: '',
      smsConsent: false
    };
  });
  // Add effect to scroll to top whenever question changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    // Reset auto-advance flag when question changes
    setHasAutoAdvanced(false);
  }, [currentQuestionIndex]);
  // Save quiz data to localStorage whenever formData changes
  useEffect(() => {
    // Extract only quiz-related fields (exclude contact info)
    const quizData: Partial<QuizData> = {
      industry: formData.industry,
      industryOther: formData.industryOther,
      isOwner: formData.isOwner,
      state: formData.state,
      yearsInBusiness: formData.yearsInBusiness,
      revenue: formData.revenue,
      profit: formData.profit,
      locations: formData.locations,
      providers: formData.providers,
      payerMix: formData.payerMix,
      concentration: formData.concentration,
      trend: formData.trend,
      timeline: formData.timeline,
      ownerHours: formData.ownerHours
    };
    // Only save if at least one field is filled
    const hasData = Object.values(quizData).some(value => value !== '' && value !== undefined && (Array.isArray(value) ? value.length > 0 : true));
    if (hasData) {
      saveQuizData(quizData);
    }
  }, [formData]);
  /**
   * BUILD GHL IFRAME SRC WHEN CONTACT QUESTION LOADS
   */
  useEffect(() => {
    const currentQuestion = questions[currentQuestionIndex];
    if (currentQuestion.id === 'contact') {
      // Base GHL form URL (without parameters)
      const baseFormUrl = 'https://api.leadconnectorhq.com/widget/form/2WonMk9TvZ0WMulwjg2R';
      // Build complete URL with all tracking params and quiz data
      const completeSrc = buildGHLIframeSrc(baseFormUrl);
      // Set the iframe src (this triggers iframe render with params)
      setGhlIframeSrc(completeSrc);
      console.log('[QuizForm] Contact question loaded - GHL iframe src built with all parameters');
    }
  }, [currentQuestionIndex, questions]);
  // Listen for GHL form submission
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Listen for GHL form submission event
      if (event.data.type === 'form-submitted' || event.data.formSubmitted) {
        // Navigate to results with quiz data from localStorage
        const savedQuizData = getQuizData();
        navigate('/results', {
          state: savedQuizData
        });
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [navigate]);
  // Check if current question has a valid answer
  const isCurrentQuestionValid = () => {
    const currentQuestion = questions[currentQuestionIndex];
    if (currentQuestion.id === 'contact') return true;
    if (currentQuestion.type === 'checkbox') {
      // For checkbox questions (like addBacks), we don't require an answer
      return true;
    } else if (currentQuestion.type === 'text') {
      // For text questions, require non-empty value
      const value = formData[currentQuestion.id as keyof typeof formData];
      return value !== '' && value !== undefined;
    } else if (currentQuestion.type === 'select') {
      // For select questions, check if value exists
      const value = formData[currentQuestion.id as keyof typeof formData];
      const isValid = value !== '' && value !== undefined;
      // Special case: if industry is "other", also require industryOther to be filled
      if (currentQuestion.id === 'industry' && value === 'other') {
        return isValid && formData.industryOther !== '' && formData.industryOther !== undefined;
      }
      return isValid;
    } else {
      // For radio and select questions, we require a non-empty value
      const value = formData[currentQuestion.id as keyof typeof formData];
      return value !== '' && value !== undefined;
    }
  };
  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      // Play the congratulatory ding sound
      playDing();
      setStepDirection('forward');
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };
  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setStepDirection('back');
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLastQuestion) {
      handleNext();
    }
    // For the last question, GHL form handles submission
  };
  // Handle option selection for radio and select questions
  const handleOptionSelect = (questionId: string, value: string) => {
    setFormData({
      ...formData,
      [questionId]: value
    });
    // Auto-advance for radio and select questions only, and only if we haven't already auto-advanced
    const currentQuestion = questions[currentQuestionIndex];
    // Special case: Don't auto-advance if industry="other" is selected (user needs to fill industryOther)
    if (questionId === 'industry' && value === 'other') {
      return; // Don't auto-advance, let user fill in the custom industry field
    }
    if (currentQuestion.type !== 'checkbox' && currentQuestion.type !== 'text' && !isLastQuestion && !hasAutoAdvanced) {
      // Mark that we've auto-advanced for this question
      setHasAutoAdvanced(true);
      // Add a slight delay before advancing to the next question
      // This gives visual feedback that the selection was made
      setTimeout(() => {
        playDing();
        setStepDirection('forward');
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      }, 300);
    }
  };
  // Handle text input change (doesn't auto-advance)
  const handleTextChange = (questionId: string, value: string) => {
    setFormData({
      ...formData,
      [questionId]: value
    });
  };
  // Handle checkbox selection (doesn't auto-advance)
  const handleCheckboxSelect = (questionId: string, values: string[]) => {
    setFormData({
      ...formData,
      [questionId]: values
    });
  };
  // Get animation class based on step direction
  const getStepAnimationClass = () => {
    return stepDirection === 'forward' ? 'animate-slide-in-right' : 'animate-slide-in-left';
  };
  // Get the current section name
  const getCurrentSectionName = () => {
    return questions[currentQuestionIndex].section;
  };
  // Render the current question based on its type
  const renderCurrentQuestion = () => {
    const currentQuestion = questions[currentQuestionIndex];
    // Special case for contact information (show GHL form)
    if (currentQuestion.id === 'contact' && ghlIframeSrc) {
      return <div className="space-y-4 md:space-y-6 animate-fade-in" data-id="element-199">
          <div style={{
          minHeight: '800px'
        }} data-id="element-200">
            <iframe src={ghlIframeSrc} style={{
            width: '100%',
            height: '800px',
            border: 'none',
            borderRadius: '3px'
          }} id="inline-2WonMk9TvZ0WMulwjg2R" data-layout="{'id':'INLINE'}" data-trigger-type="alwaysShow" data-trigger-value="" data-activation-type="alwaysActivated" data-activation-value="" data-deactivation-type="neverDeactivate" data-deactivation-value="" data-form-name="Valuation Calculator Form" data-height="undefined" data-layout-iframe-id="inline-2WonMk9TvZ0WMulwjg2R" data-form-id="2WonMk9TvZ0WMulwjg2R" title="Valuation Calculator Form" data-id="element-201" />
          </div>
        </div>;
    }
    // Render based on question type
    switch (currentQuestion.type) {
      case 'radio':
        return <div className={`${getStepAnimationClass()}`} data-id="element-202">
            <RadioGroup name={currentQuestion.id} label={currentQuestion.label} options={currentQuestion.options || []} value={formData[currentQuestion.id as keyof typeof formData] as string} onChange={value => handleOptionSelect(currentQuestion.id, value)} required autoAdvance={true} data-id="element-203" />
          </div>;
      case 'select':
        return <div className={`${getStepAnimationClass()}`} data-id="element-204">
            <Select name={currentQuestion.id} label={currentQuestion.label} options={currentQuestion.options || []} value={formData[currentQuestion.id as keyof typeof formData] as string} onChange={value => handleOptionSelect(currentQuestion.id, value)} required tooltip={currentQuestion.tooltip ? <Tooltip content={currentQuestion.tooltip} data-id="element-206" /> : undefined} autoAdvance={true} data-id="element-205" />

            {/* Conditional "Other" text input for industry question */}
            {currentQuestion.id === 'industry' && formData.industry === 'other' && <div className="mt-4 animate-fade-in" data-id="element-207">
                  <TextInput name="industryOther" label="Please specify your industry" value={formData.industryOther || ''} onChange={value => handleTextChange('industryOther', value)} placeholder="e.g., Veterinary, Optometry, etc." type="text" data-id="element-208" />
                </div>}
          </div>;
      case 'text':
        return <div className={`${getStepAnimationClass()}`} data-id="element-209">
            <TextInput name={currentQuestion.id} label={currentQuestion.label} value={formData[currentQuestion.id as keyof typeof formData] as string} onChange={value => handleTextChange(currentQuestion.id, value)} required placeholder={currentQuestion.placeholder} type="number" tooltip={currentQuestion.tooltip ? <Tooltip content={currentQuestion.tooltip} data-id="element-211" /> : undefined} data-id="element-210" />
          </div>;
      case 'checkbox':
        return <div className={`${getStepAnimationClass()}`} data-id="element-212">
            <CheckboxGroup name={currentQuestion.id} label={currentQuestion.label} options={currentQuestion.options || []} values={formData[currentQuestion.id as keyof typeof formData] as string[]} onChange={values => handleCheckboxSelect(currentQuestion.id, values)} tooltip={currentQuestion.tooltip ? <Tooltip content={currentQuestion.tooltip} data-id="element-214" /> : undefined} data-id="element-213" />
          </div>;
      default:
        return null;
    }
  };
  return <div className="min-h-screen bg-white py-6 md:py-12 px-4" data-id="element-215">
      <div className="max-w-2xl mx-auto" data-id="element-216">
        {/* Logo at top */}
        <div className="animate-fade-in mb-6 md:mb-8 text-center" data-id="element-217">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/qmxPrQcAoJQeBtBGYhM9D6/516lion.png" alt="516 Partners Logo" className="h-16 md:h-20 mx-auto" data-id="element-218" />
        </div>

        <div className="bg-white border border-gray-200 p-4 sm:p-6 md:p-8 lg:p-12 animate-fade-in-scale" data-id="element-219">
          {/* Progress Bar */}
          <ProgressBar currentStep={currentQuestionIndex + 1} totalSteps={totalQuestions} data-id="element-220" />

          {/* Section Title */}
          <div className="mb-6 md:mb-8 animate-fade-in" data-id="element-221">
            <h2 className="text-xl sm:text-2xl font-serif font-bold text-gray-900 mb-2" data-id="element-222">
              {isLastQuestion ? 'Get your results' : getCurrentSectionName()}
            </h2>
            <p className="text-sm sm:text-base text-gray-600" data-id="element-223">
              {isLastQuestion ? "We'll email your valuation preview right away" : `Question ${currentQuestionIndex + 1} of ${totalQuestions}`}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8" data-id="element-224">
            {/* Current Question */}
            {renderCurrentQuestion()}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between pt-6 md:pt-8 border-t border-gray-200" data-id="element-225">
              {currentQuestionIndex > 0 ? <button type="button" onClick={handleBack} className="flex items-center space-x-2 px-4 sm:px-6 py-3 text-sm sm:text-base text-gray-700 font-medium border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 transition-colors" disabled={isSubmitting} data-id="element-226">
                  <ArrowLeftIcon className="w-4 h-4" data-id="element-227" />
                  <span data-id="element-228">Back</span>
                </button> : <div data-id="element-229"></div>}

              {/* Show Next button for all questions except the last one */}
              {!isLastQuestion && <button type="button" onClick={handleNext} disabled={!isCurrentQuestionValid()} className="flex items-center space-x-2 px-6 sm:px-8 py-3 text-sm sm:text-base bg-yellow-500 text-gray-900 font-semibold hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors ml-auto" data-id="element-230">
                  <span data-id="element-231">Next</span>
                  <ArrowRightIcon className="w-4 h-4" data-id="element-232" />
                </button>}
            </div>

            {isLastQuestion && <p className="text-xs text-center text-gray-500 pt-4 animate-fade-in" data-id="element-233">
                This is a preview, not an appraisal. For owners with $1M+
                revenue.
              </p>}
          </form>
        </div>
      </div>

      {/* GHL Form Embed Script */}
      {isLastQuestion && ghlIframeSrc && <script src="https://link.msgsndr.com/js/form_embed.js" async data-id="element-234" />}
    </div>;
}